import * as React from 'react';
import { EditingStyle, UpdateEditingStyleInput } from '../../types';
import { isValidEditingStyleName, readName } from '../../editingStyleName';
import { readDescription } from '../../editingStyleDescription';

type UseEditEditingStyleFormArgs = {
    editingStyle: EditingStyle;
    onEditSave: (id: number, input: UpdateEditingStyleInput) => void;
    onClose: () => void;
};

type EditEditingStyleFormState = {
    isDirty: boolean;
    handleChange: (event: React.FormEvent<HTMLFormElement>) => void;
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

export function readInput(form: HTMLFormElement): UpdateEditingStyleInput {
    let formData = new FormData(form);

    return {
        name: readName(formData.get('name')),
        description: readDescription(formData.get('description')),
        isActive: formData.get('isActive') === 'on'
    };
}

export function hasChanges(input: UpdateEditingStyleInput, editingStyle: EditingStyle): boolean {
    return (
        input.name !== editingStyle.name ||
        input.description !== editingStyle.description ||
        input.isActive !== editingStyle.isActive
    );
}

export function canSave(input: UpdateEditingStyleInput, editingStyle: EditingStyle): boolean {
    return isValidEditingStyleName(input.name) && hasChanges(input, editingStyle);
}

export function useEditEditingStyleForm({
    editingStyle,
    onEditSave,
    onClose
}: UseEditEditingStyleFormArgs): EditEditingStyleFormState {
    const [isDirty, setIsDirty] = React.useState(false);
    const submitted = React.useRef(false);

    function handleChange(event: React.FormEvent<HTMLFormElement>) {
        setIsDirty(canSave(readInput(event.currentTarget), editingStyle));
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (submitted.current) {
            return;
        }

        submitted.current = true;
        onEditSave(editingStyle.id, readInput(event.currentTarget));
        onClose();
    }

    return { isDirty, handleChange, handleSubmit };
}
